import { createPortal } from "react-dom";
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { X } from "@phosphor-icons/react";

type MapActionSheetProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
};

export function MapActionSheet({
  open,
  title,
  onClose,
  children,
}: MapActionSheetProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const startYRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setDragOffset(0);
      startYRef.current = null;
    }
  }, [open]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!open) return;
    startYRef.current = event.clientY;
    setIsDragging(true);
    setDragOffset(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging || startYRef.current == null) return;
    const delta = Math.max(0, event.clientY - startYRef.current);
    setDragOffset(delta);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const shouldClose = dragOffset > 80;
    setIsDragging(false);
    setDragOffset(0);
    startYRef.current = null;
    if (shouldClose) {
      onClose();
    }
  };

  if (typeof document === "undefined") {
    return null;
  }

  const backdropClasses =
    "absolute inset-0 cursor-pointer border-none bg-slate-950/60 transition-opacity duration-200";
  const panelClasses =
    "absolute left-0 right-0 bottom-0 flex flex-col gap-4 rounded-t-3xl bg-white p-6 shadow-[0_-20px_60px_rgba(15,23,42,0.35)] transition-transform duration-200 dark:bg-slate-900";
const dragHandleClasses =
  "flex w-full items-center justify-center pt-1 pb-3 touch-none select-none";
const dragBarClasses =
  "h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-600";
  const headerClasses =
    "flex items-center justify-between gap-3 text-slate-900 dark:text-slate-100";
  const closeButtonClasses =
    "inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700";
  const bodyClasses = "overflow-y-auto pr-1 text-slate-800 dark:text-slate-200";

  const sheet = (
    <div
      className={[
        "fixed inset-0 z-[980] transition duration-200",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        className={[
          backdropClasses,
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={panelClasses}
        style={{
          transform: open
            ? `translateY(${dragOffset}px)`
            : "translateY(100%)",
          transition: isDragging ? "none" : undefined,
        }}
      >
        <div
          className={dragHandleClasses}
          role="button"
          tabIndex={0}
          aria-label="Drag down to close"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClose();
            }
          }}
        >
          <span className={dragBarClasses} aria-hidden />
        </div>
        <div className={headerClasses}>
          <h3 className="text-base font-semibold">{title}</h3>
          <button
            type="button"
            className={closeButtonClasses}
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className={bodyClasses}>{children}</div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
